import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clapperboard, Check, Loader2, ArrowRight } from "lucide-react";

export default function CreatorOnboard() {
  const [f, setF] = useState({
    display_name: "", email: "", password: "", organization: "", bio: "",
    tier_key: "BASE_TIER", boards: "", is_hybrid_educator: false,
    country_code: "IN", state: "", district: "", city: "", postal_code: "",
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/geo/countries").then(r => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/membership/plans/creator").then(r => {
      setTiers(r.data);
      if (r.data[0]) setF(v => ({ ...v, tier_key: r.data[0].key }));
    }).catch(() => {});
  }, []);
  useEffect(() => { setF(v => ({ ...v, state: "", district: "" })); setStates([]); setDistricts([]); if (f.country_code) api.get("/geo/states", { params: { country_code: f.country_code } }).then(r => setStates(r.data)).catch(() => {}); }, [f.country_code]);
  useEffect(() => { setF(v => ({ ...v, district: "" })); setDistricts([]); if (f.country_code && f.state) api.get("/geo/districts", { params: { country_code: f.country_code, state: f.state } }).then(r => setDistricts(r.data)).catch(() => {}); }, [f.state]);

  const submit = async () => {
    if (!f.display_name || !f.email || f.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required"); return;
    }
    setBusy(true);
    try {
      const boards = f.boards.split(",").map(b => b.trim()).filter(Boolean);
      const r = await api.post("/creators/onboard", { ...f, boards });
      setDone(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Onboarding failed"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-[#FAF7F0] grid place-items-center p-6">
      <div className="nb-card bg-[#86EFAC] p-8 max-w-lg w-full space-y-4 text-center" data-testid="creator-onboard-success">
        <div className="w-16 h-16 rounded-full bg-white border-2 border-black grid place-items-center mx-auto"><Check size={32} /></div>
        <h1 className="display text-3xl font-black">You're a Ponderr Creator!</h1>
        <p className="text-sm">Your studio is ready on the <b>{done.tier_key}</b> plan. Status: <b>{done.status}</b>.</p>
        <div className="nb-card bg-white p-4 text-left text-sm mono"><div>Login: <b>{done.email}</b></div></div>
        <Link to="/login" className="nb-btn w-full justify-center" data-testid="creator-goto-login">Go to Login <ArrowRight size={16} /></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F0] p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Clapperboard size={28} /><h1 className="display text-3xl font-black">Create on Ponderr TV</h1></div>
          <Link to="/tv" className="text-xs underline" data-testid="creator-view-tv">Watch Ponderr TV →</Link>
        </div>
        <div className="nb-card bg-white p-6 space-y-3">
          <input className="nb-input" placeholder="Display Name / Channel *" value={f.display_name} onChange={set("display_name")} data-testid="creator-name" />
          <div className="grid md:grid-cols-2 gap-3">
            <input className="nb-input" placeholder="Login Email *" value={f.email} onChange={set("email")} data-testid="creator-email" />
            <input type="password" className="nb-input" placeholder="Password (6+) *" value={f.password} onChange={set("password")} data-testid="creator-password" />
            <input className="nb-input" placeholder="Organization" value={f.organization} onChange={set("organization")} data-testid="creator-org" />
            <input className="nb-input" placeholder="Boards (comma-sep e.g. CBSE)" value={f.boards} onChange={set("boards")} data-testid="creator-boards" />
          </div>
          <textarea className="nb-input" rows="2" placeholder="Short bio" value={f.bio} onChange={set("bio")} data-testid="creator-bio" />

          <h2 className="display text-lg font-bold pt-2">Service Location</h2>
          <p className="text-xs opacity-60 -mt-1">Scopes your physical doubt-desk & local presence to the student districts you serve.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <select className="nb-select" value={f.country_code} onChange={set("country_code")} data-testid="creator-country">
              {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
            </select>
            <select className="nb-select" value={f.state} onChange={set("state")} disabled={!states.length} data-testid="creator-state">
              <option value="">Select State</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="nb-select" value={f.district} onChange={set("district")} disabled={!districts.length} data-testid="creator-district">
              <option value="">Select District</option>
              {districts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {f.state && districts.length === 0 &&
              <p className="text-xs text-amber-700 md:col-span-2" data-testid="creator-nodistricts">No districts added yet — add via Global Admin Territories.</p>}
            <input className="nb-input" placeholder="City" value={f.city} onChange={set("city")} data-testid="creator-city" />
            <input className="nb-input mono" placeholder="Postal / ZIP" value={f.postal_code} onChange={set("postal_code")} data-testid="creator-postal" />
          </div>

          <label className="nb-label pt-2">Plan / Tier</label>
          <select className="nb-select" value={f.tier_key} onChange={set("tier_key")} data-testid="creator-tier">
            {tiers.map(t => (
              <option key={t.key} value={t.key}>
                {t.name} — {t.currency === "USD" ? "$" : "₹"}{Number(t.effective_price ?? t.base_price).toLocaleString()}/{t.billing_interval === "monthly" ? "mo" : "yr"}{t.trial_days > 0 ? ` · ${t.trial_days}-day trial` : ""}
              </option>
            ))}
          </select>
          {(() => { const t = tiers.find(x => x.key === f.tier_key); return t ? (
            t.features?.length
              ? <ul className="text-xs mt-1 space-y-0.5" data-testid="creator-tier-features">{t.features.map((x, i) => <li key={i}>✓ {x}</li>)}</ul>
              : <p className="text-xs mt-1 opacity-50" data-testid="creator-tier-features">No feature list configured for this tier yet.</p>
          ) : null; })()}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_hybrid_educator} onChange={e => setF({ ...f, is_hybrid_educator: e.target.checked })} data-testid="creator-hybrid" />
            I'm a Hybrid Educator (Coach/Tutor) — enable 70/30 revenue split
          </label>
          <button className="nb-btn w-full justify-center" onClick={submit} disabled={busy} data-testid="creator-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Launch My Studio"}
          </button>
        </div>
      </div>
    </div>
  );
}
